/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AccountsApiService, OrganisationsApiService } from '../../../shared/http';
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { LogService, RbacService, UserService } from '../../../../../core/services';
import { PortalUrlsEnum } from '../../../shared/enums';
import { environment } from '../../../../../../environments/environment';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-accounts-sso',
    templateUrl: './accounts-sso.component.html',
    styleUrls: ['./accounts-sso.component.scss']
})

export class AccountsSsoComponent extends BaseComponent {
    /**
     * Has tier
     */
    public hasTier: boolean = false;

    /**
     * Store the name of the SSO provider.
     */
    public ssoProvider: string = null;

    /**
     * Store the sso login state
     */
    public userLogin: string = '';

    /**
     * Store if SAML is enforced.
     */
    public samlEnforced: boolean = false;

    /**
     * Store the user for user across component.
     * @private
     */
    private readonly user: any = null;

    constructor(
        private accountsApiService: AccountsApiService,
        private logService: LogService,
        private organisationApiService: OrganisationsApiService,
        private rbacService: RbacService,
        private userService: UserService
    ) {
        super();

        this.hasTier = this.rbacService.getPageRequiredRoute('/api/v1/accounts/:id/auth')?.tier;
        this.user = this.userService.getUser();
        this.setUpPageData();
    }

    /**
     * Redirect user to SSO sign in.
     * @param brand
     */
    public linkToSSOProvider(brand: string): void {
        this.userService.setUserValueByProperty('ssoProvider', brand);
        this.setLocalUserValues();
        window.location.href = environment.production
            ? '/api/v1/accounts/auth/' + brand
            : PortalUrlsEnum.NON_PRODUCTION + '/api/v1/accounts/auth/' + brand;
    }

    /**
     * Remove link to SSO provider and update the user service user object.
     */
    public removeLinkToSSOProvider(): void {
        this.accountsApiService.deleteSSO(this.user.id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    delete this.user.ssoProvider;
                    this.userService.user = this.user;
                    this.ssoProvider = null;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'AccountsSsoComponent:removeLinkToSSOProvider',
                        'Unable to remove SSO provide',
                        error
                    );
                }
            });
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        this.organisationApiService.get(this.user.organisationId)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (orgDetails: any): void => {
                    this.samlEnforced = orgDetails?.samlEnforced;

                    this.setLocalUserValues();
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'AccountsSsoComponent:setUpPageData',
                        'Unable to get organisation SSO provider.' + error
                    );
                }
            });
    }

    /**
     * Set local variables based on user service.
     * @private
     */
    private setLocalUserValues(): void {
        this.ssoProvider = this.user.ssoProvider;
        this.userLogin = this.user.userLogin;
    }
}
